import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import Logo from './Logo';
import MagneticButton from './MagneticButton';
import { useSite } from '../context/SiteContext';
import { pathFor } from '../i18n/routes';

const NAV_KEYS = ['home', 'media', 'souvenir', 'portfolio', 'about', 'contact'];

export function LangSwitch({ className = '' }) {
  const { lang, routeKey, t } = useSite();
  const navigate = useNavigate();
  const other = lang === 'pt' ? 'en' : 'pt';
  return (
    <div className={`flex items-center gap-1 font-mono text-xs tracking-widest ${className}`} data-testid="lang-switch">
      {['pt', 'en'].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => {
            localStorage.setItem('fl_lang', l);
            navigate(pathFor(routeKey || 'home', l));
          }}
          className={`px-1.5 py-1 uppercase transition-colors duration-300 ${lang === l ? 'brand-text font-semibold' : 'text-zinc-500 hover:text-white'}`}
          aria-pressed={lang === l}
          aria-label={l === 'pt' ? 'Português' : 'English'}
        >
          {l}
        </button>
      ))}
      <span className="sr-only">{other}</span>
    </div>
  );
}

export default function Navbar() {
  const { lang, routeKey, t } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-[90] transition-all duration-500 ${
          scrolled ? 'border-b border-zinc-900 bg-black/80 backdrop-blur-xl' : 'bg-transparent'
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 sm:h-20 sm:px-8">
          <Link to={pathFor('home', lang)} data-testid="nav-logo" aria-label="F&L Group" data-cursor={t.cursor.open}>
            <Logo />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Principal">
            {NAV_KEYS.map((key) => (
              <Link
                key={key}
                to={pathFor(key, lang)}
                data-testid={`nav-${key}`}
                data-cursor={t.cursor.open}
                className={`relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  routeKey === key ? 'brand-text' : 'text-zinc-400 hover:text-white'
                }`}
              >
                {t.nav[key === 'home' ? 'home' : key]}
                {routeKey === key && <span className="absolute -bottom-2 left-0 h-px w-full brand-bg" />}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-6 lg:flex">
            <LangSwitch />
            <MagneticButton
              to={pathFor('contact', lang)}
              testId="nav-cta"
              cursor={t.cursor.contact}
              className="border border-zinc-700 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 brand-border hover:text-black hover:!bg-[var(--brand)]"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </MagneticButton>
          </div>

          <button
            className="text-white lg:hidden"
            onClick={() => setOpen(true)}
            aria-label={t.nav.openMenu}
            data-testid="nav-menu-open"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[120] flex flex-col bg-black/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <Logo />
              <button onClick={() => setOpen(false)} aria-label={t.nav.closeMenu} data-testid="nav-menu-close" className="text-white">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Menu móvel">
              {NAV_KEYS.map((key, i) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5 }}
                >
                  <Link
                    to={pathFor(key, lang)}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${key}`}
                    className={`block py-2 font-heading text-4xl font-extrabold uppercase tracking-tight transition-colors ${
                      routeKey === key ? 'brand-text' : 'text-white'
                    }`}
                  >
                    {t.nav[key === 'home' ? 'home' : key]}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-8 flex items-center justify-between border-t border-zinc-800 pt-6"
              >
                <LangSwitch className="text-sm" />
                <Link
                  to={pathFor('contact', lang)}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta"
                  className="font-mono text-xs uppercase tracking-[0.2em] brand-text"
                >
                  {t.nav.cta} →
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
